// DateTimeField — контролируемое поле даты+времени (MUI X DateTimePicker, локаль ru).
// Для useState-форм (диалоги) без react-hook-form; RHF-вариант — DateTimePickerField.
// Значение — ISO Instant string | null ('' → null); отображение — dd.MM.yyyy HH:mm.
// Popper MUI X (PickersPopper: zIndex = theme.zIndex.modal) позиционируется над
// модальным Dialog в отличие от UA-календаря <input type="datetime-local"> (V15-a).

import { useState, type ReactNode } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers';
import { parseFieldValue, toIsoInstant } from './dateValue';
import { t } from '@/i18n';

interface DateTimeFieldProps {
  /** Label поля. */
  label: string;
  /** ISO Instant string (или null — «не выбрано»). */
  value: string | null;
  /** Колбэк выбора/очистки: ISO Instant string | null. */
  onChange: (iso: string | null) => void;
  /** Обязательное поле. */
  required?: boolean;
  /** Подсказка под полем (например, «срок нельзя очистить» в Edit). */
  helperText?: string;
  /** Кнопка очистки «×» (родная MUI X-очистка field-компонента через `slotProps.field.clearable`; default false — кнопка не рендерится). */
  clearable?: boolean;
  /** ARIA-label для a11y. */
  'aria-label'?: string;
}

/** Поле выбора даты и времени (MUI X) для диалогов с useState. */
export function DateTimeField({
  label,
  value,
  onChange,
  required = false,
  helperText,
  clearable = false,
  'aria-label': ariaLabel,
}: DateTimeFieldProps): ReactNode {
  // F-2 (pintest): невалидная дата («31.02.2026») → error-текст под полем вместо тихого сброса.
  const [dateInvalid, setDateInvalid] = useState(false);
  return (
    <DateTimePicker
      value={parseFieldValue(value)}
      onChange={(next: Date | null, context) => {
        onChange(toIsoInstant(next));
        setDateInvalid(context.validationError === 'invalidDate');
      }}
      format="dd.MM.yyyy HH:mm"
      ampm={false}
      slotProps={{
        textField: {
          label,
          required,
          fullWidth: true,
          error: dateInvalid,
          helperText: dateInvalid ? t('forms.dateInvalid') : helperText,
          inputProps: ariaLabel ? { 'aria-label': ariaLabel } : undefined,
        },
        // Родная очистка MUI X (W2-challenge #7): проп `clearable` существует только у
        // field-компонента (useClearableField.d.ts:11), НЕ у DateTimePicker
        // (DateTimePickerProps его не содержит — прямой проп вызвал бы TS2322).
        // Включаем через slotProps.field: внутри поля кнопка «×» (класс .clearButton)
        // рендерится ПЕРЕД иконкой календаря (endAdornment-фрагмент) — обе кнопки видны;
        // при пустом значении кнопка скрыта (useField.js:222), клик → onChange(null).
        field: { clearable },
        // Popper под полем (канон MUI X над модальным диалогом; не UA-календарь).
        popper: { placement: 'bottom-start' },
      }}
    />
  );
}
